<template>
  <el-dialog
    :model-value="visible"
    :title="isEdit ? '製品自動マッピング編集' : '製品自動マッピング登録'"
    width="860px"
    append-to-body
    destroy-on-close
    @close="onCancel"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="120px"
      size="large"
    >
      <el-form-item label="MCCS ver" prop="deviceType">
        <el-select
          v-model="form.deviceType"
          placeholder="選択"
          style="width: 100%"
          :loading="productLoading"
          @change="onDeviceTypeChange"
        >
          <el-option
            v-for="opt in orderAutoMapDeviceTypeOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="製品名" prop="productId">
        <el-select
          v-model="form.productId"
          filterable
          clearable
          placeholder="選択"
          style="width: 100%"
          :loading="productLoading"
        >
          <el-option
            v-for="product in productList"
            :key="product.id"
            :label="product.name"
            :value="product.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item
        label="価格マッピング"
        prop="isUseMasterPrice"
        class="price-mapping-item"
        label-width="120px"
      >
        <el-radio-group v-model="form.isUseMasterPrice">
          <el-radio :label="true">マスタ価格参照</el-radio>
          <el-radio :label="false">SF価格参照</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-form-item label="条件" prop="group">
        <ConditionBuilder
          :group="form.group"
          form-prefix="group"
          @update:group="form.group = $event"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button size="large" @click="onCancel">キャンセル</el-button>
      <el-button size="large" type="primary" @click="onSubmit">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
  import { ref, watch, computed, nextTick } from 'vue';
  import { useStore } from 'vuex';
  import ConditionBuilder from './ConditionBuilder.vue';
  import type { FormInstance, FormRules } from 'element-plus';
  import type {
    ConditionGroup,
    OrderAutoMapFormData,
    OrderAutoMapRow
  } from '@/types/master/orderAutoMap';
  import type { ProductsDataState } from '@/types/master/product';
  import {
    parseConditionGroup,
    createDefaultConditionGroup,
    orderAutoMapDeviceTypeOptions
  } from '@/libs/orderAutoMap';

  type FormState = {
    id?: number;
    deviceType: number | null;
    productId: number | null;
    isUseMasterPrice: boolean;
    group: ConditionGroup;
  };

  const props = defineProps<{
    visible: boolean;
    row: OrderAutoMapRow | null;
    productList: ProductsDataState[];
  }>();
  const emit = defineEmits(['save', 'cancel']);

  const store = useStore();
  const productLoading = ref(false);
  const formRef = ref<FormInstance>();
  const form = ref<FormState>({
    deviceType: 3,
    productId: null,
    isUseMasterPrice: false,
    group: createDefaultConditionGroup()
  });

  const isEdit = computed(() => props.row != null);

  const rules = computed<FormRules>(() => ({
    deviceType: [{ required: true, message: 'MCCS verは必須です' }],
    productId: [{ required: true, message: '製品名は必須です' }],
    group: [
      {
        validator: (_rule, value: ConditionGroup, callback) => {
          if (!value?.rules?.length) {
            callback(new Error('条件は必須です'));
            return;
          }
          callback();
        }
      }
    ]
  }));

  const createDefaultForm = (): FormState => ({
    deviceType: 3,
    productId: null,
    isUseMasterPrice: false,
    group: createDefaultConditionGroup()
  });

  const createFormFromRow = (row: OrderAutoMapRow): FormState => ({
    id: row.id,
    deviceType: row.deviceType ?? 3,
    productId: row.productId,
    isUseMasterPrice: row.isUseMasterPrice ?? false,
    group: parseConditionGroup(row.condition)
  });

  const loadProductsByDeviceType = async (deviceType: number) => {
    productLoading.value = true;
    try {
      await store.dispatch('productsMaster/fetchByDeviceType', deviceType);
      if (
        form.value.productId != null &&
        !props.productList.some(
          (product) => product.id === form.value.productId
        )
      ) {
        form.value.productId = null;
      }
    } finally {
      productLoading.value = false;
    }
  };

  watch(
    () => props.visible,
    async (val) => {
      if (val) {
        form.value = props.row
          ? createFormFromRow(props.row)
          : createDefaultForm();
        await nextTick();
        formRef.value?.clearValidate();
        if (form.value.deviceType != null) {
          await loadProductsByDeviceType(form.value.deviceType);
        }
      }
    }
  );

  const onDeviceTypeChange = async () => {
    if (form.value.deviceType == null) return;
    form.value.productId = null;
    await loadProductsByDeviceType(form.value.deviceType);
  };

  const onSubmit = async () => {
    if (!formRef.value) return;
    try {
      await formRef.value.validate();
      const payload: OrderAutoMapFormData = {
        id: form.value.id,
        deviceType: form.value.deviceType,
        productId: form.value.productId,
        isUseMasterPrice: form.value.isUseMasterPrice,
        condition: JSON.stringify(form.value.group)
      };
      emit('save', payload);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const onCancel = () => emit('cancel');
</script>

<style scoped lang="scss">
  .price-mapping-item {
    margin-bottom: 24px;

    :deep(.el-form-item__content) {
      width: fit-content;
      line-height: 1;
    }

    :deep(.el-radio-group) {
      display: flex;
      gap: 12px;
    }

    :deep(.el-radio) {
      margin-right: 0;
    }
  }
</style>
